// @erp/ui/lookup-select — a select whose OPTIONS COME FROM THE SERVER, for a
// registry too large to hand the page in full (subjects, libraries,
// publishers: lists that grow).
//
// It behaves like an ordinary select and nothing else: click the trigger and a
// list opens, already populated. The difference is a search field at the TOP OF
// THAT LIST which re-queries the server as you type. That ordering matters —
// the sibling @erp/ui/searchable-select is type-FIRST (an input that shows
// nothing until you type), which is right for "find the one record I mean" and
// wrong for a filter, where a reader wants to see what the options ARE before
// deciding to narrow them.
//
// Why not extend FilterSelect: that component's options are a prop, and
// everything it does — the initial active index, the keyboard walk, the closed
// label — assumes the full list is already in hand and never changes. Here the
// list arrives late, changes per keystroke, and can arrive out of order. Those
// are different enough to be a different component; what they share is the
// trigger's look, which is deliberately copied class for class so a page of
// filters shows one control, not two that nearly match.
import { For, Show, createSignal, createUniqueId, onCleanup } from 'solid-js';
import { ChevronDown, Check, Search } from 'lucide-solid';
import { Spinner } from '../spinner/index.js';
import { ProgressBar } from '../progress-bar/index.js';
import { inputClass, useFieldControl } from '../field/index.js';
import type { FilterOption } from '../filter-select/index.js';

export interface LookupSelectProps {
  value: string;
  /**
   * The current value's label. It has to be passed, not looked up: the chosen
   * option is usually absent from whatever slice the server last returned.
   */
  valueLabel?: string;
  /** Debounced, capped, server-side. Called with '' when the list opens. */
  search: (query: string) => Promise<FilterOption[]>;
  onChange: (value: string, label: string) => void;
  /**
   * The "no choice" row, pinned above the results and never filtered away —
   * "All subjects". Omit it and the control cannot be cleared from its own
   * list, which is right when a value is mandatory.
   */
  emptyLabel?: string;
  /** Placeholder for the trigger when nothing is chosen. */
  placeholder?: string;
  searchPlaceholder?: string;
  /** Debounce window in ms (default 250). */
  debounce?: number;
  minWidth?: string;
  size?: 'sm' | 'md';
  class?: string;
  id?: string;
  /**
   * The trigger's accessible name. `role="combobox"` takes no name from its
   * content, so without one the control is a nameless button (axe
   * `button-name`, critical — the 2026-09-20 finding on the issue form's
   * "Belongs to" picker). Inside a Field the Field's label is adopted, as
   * TextInput/Select do; this prop wins over it. Outside a Field with neither,
   * the id's last segment sentence-cased is used, FilterSelect's convention
   * (`input-trail-actor` → "Actor"), so the control is never nameless.
   */
  'aria-label'?: string;
}

export function LookupSelect(props: LookupSelectProps) {
  const uid = createUniqueId();
  const baseId = () => props.id ?? `lookup-select-${uid}`;
  const optId = (i: number) => `${baseId()}-opt-${i}`;

  const [open, setOpen] = createSignal(false);
  const [query, setQuery] = createSignal('');
  const [results, setResults] = createSignal<FilterOption[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [active, setActive] = createSignal(0);

  let root: HTMLDivElement | undefined;
  let trigger: HTMLButtonElement | undefined;
  let field: HTMLInputElement | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  // Monotonic request id: a slow response for "co" must never overwrite the
  // list for "comp" typed after it.
  let reqId = 0;

  /** The empty row is part of the list for keyboard/index purposes. */
  const rows = (): FilterOption[] =>
    props.emptyLabel !== undefined ? [{ value: '', label: props.emptyLabel }, ...results()] : results();

  const run = (q: string): void => {
    const mine = ++reqId;
    setLoading(true);
    void props
      .search(q)
      .then((r) => {
        if (mine !== reqId) return;
        setResults(r);
        setActive(0);
      })
      .catch(() => {
        if (mine === reqId) setResults([]);
      })
      .finally(() => {
        if (mine === reqId) setLoading(false);
      });
  };

  const onType = (q: string): void => {
    setQuery(q);
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => run(q), props.debounce ?? 250);
  };

  const openList = (): void => {
    if (open()) return;
    setOpen(true);
    setQuery('');
    setActive(0);
    run(''); // the list is populated BEFORE a keystroke — the whole point
    // Focus the search field, not the list: typing is the fast path, and the
    // arrow keys still walk the options from here.
    queueMicrotask(() => field?.focus());
  };

  const close = (returnFocus = true): void => {
    setOpen(false);
    if (timer) clearTimeout(timer);
    if (returnFocus) trigger?.focus();
  };

  const pick = (o: FilterOption): void => {
    props.onChange(o.value, o.label);
    close();
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    const list = rows();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open()) return openList();
      if (list.length) setActive((i) => (i + 1) % list.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (list.length) setActive((i) => (i - 1 + list.length) % list.length);
    } else if (e.key === 'Home' && open()) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End' && open()) {
      e.preventDefault();
      setActive(Math.max(0, list.length - 1));
    } else if (e.key === 'Enter') {
      const o = list[active()];
      if (open() && o) {
        e.preventDefault();
        pick(o);
      }
    } else if (e.key === 'Escape' && open()) {
      e.preventDefault();
      close();
    }
  };

  const onFocusOut = (e: FocusEvent): void => {
    if (!root?.contains(e.relatedTarget as Node)) close(false);
  };

  onCleanup(() => {
    if (timer) clearTimeout(timer);
  });

  const label = () => (props.value ? (props.valueLabel ?? props.value) : (props.placeholder ?? 'Select'));

  // The Field's label / validity / help-text id reach the trigger the way they
  // reach every kit control; a getter keeps an explicit aria-label reactive.
  const ctl = useFieldControl({
    get 'aria-label'() {
      return props['aria-label'];
    },
  });
  const name = () => {
    const fromField = ctl.label();
    if (fromField) return fromField;
    const tail = (props.id ?? '').split('-').filter(Boolean).pop() ?? '';
    return tail ? tail.charAt(0).toUpperCase() + tail.slice(1) : 'Lookup';
  };

  return (
    <div class="dropdown" classList={{ 'dropdown-open': open() }} ref={root} onFocusOut={onFocusOut}>
      {/* Trigger: FilterSelect's classes verbatim, so the two controls are
          indistinguishable in a filter grid. */}
      <button
        type="button"
        ref={trigger}
        id={baseId()}
        role="combobox"
        aria-label={name()}
        aria-invalid={ctl.invalid()}
        aria-describedby={ctl.describedBy()}
        aria-haspopup="listbox"
        aria-expanded={open()}
        aria-controls={`${baseId()}-listbox`}
        onKeyDown={onKeyDown}
        onClick={() => (open() ? close() : openList())}
        class={`${props.size === 'sm' ? 'h-8 px-3 rounded-selector' : 'h-10 px-3.5 rounded-field'} inline-flex items-center justify-between gap-2 bg-base-100 border border-base-300 text-xs font-medium text-base-muted cursor-pointer hover:border-(--input-border-color) transition-colors ${props.class ?? ''}`}
        style={`min-width: ${props.minWidth ?? '150px'};`}
      >
        <span class="truncate">{label()}</span>
        <ChevronDown size={14} class="text-base-subtle shrink-0 transition-transform [.dropdown.dropdown-open_&]:rotate-180" />
      </button>

      <div
        // Popover elevation and radius match FilterSelect's sheet — one
        // floating-surface treatment across the kit (shadow-raised, rounded-700).
        class="dropdown-content [.dropdown:not(.dropdown-open)_&]:hidden z-30 mt-1.5 w-max min-w-full max-w-[320px] rounded-700 border border-base-300 bg-base-100 shadow-raised"
      >
        {/* The search field lives INSIDE the list, pinned above it. */}
        <div class="relative border-b border-base-300 p-1.5">
          <span class="pointer-events-none absolute left-4 top-1/2 flex -translate-y-1/2 text-base-subtle">
            <Search size={14} />
          </span>
          <input
            ref={field}
            type="text"
            role="searchbox"
            id={`${baseId()}-search`}
            aria-controls={`${baseId()}-listbox`}
            aria-activedescendant={open() && rows().length ? optId(active()) : undefined}
            class={`${inputClass} h-8 pl-9 text-xs`}
            placeholder={props.searchPlaceholder ?? 'Search…'}
            value={query()}
            onInput={(e) => onType(e.currentTarget.value)}
            onKeyDown={onKeyDown}
          />
        </div>

        {/* A RESERVED 4px strip above the options, never an overlay: absolute
            positioning put the bar across the first option's row, and on the
            search field's border it collided with that field's 2px focus ring.
            Always present at the bar's own height in the popover's own colour,
            so it is invisible when idle and shifts nothing when it runs. */}
        <div class="h-1 bg-base-100">
          <ProgressBar active={loading()} label="Searching options" />
        </div>
        <div>
          <ul id={`${baseId()}-listbox`} role="listbox" aria-labelledby={baseId()} class="max-h-64 space-y-1 overflow-y-auto p-1.5">
          {/* Only the FIRST fetch shows a spinner row, because there is nothing
              else to show. A re-query keeps the current options on screen and
              lets the bar say they are being updated — blanking a list someone
              is reading, once per keystroke, is worse than a slightly stale
              one. Same rule the table follows with its rows. */}
          <Show when={!loading() || rows().length > 0} fallback={
            <li class="flex items-center gap-2 px-2.5 py-3 text-xs text-base-subtle">
              <Spinner label="Searching" /> Searching…
            </li>
          }>
            <Show
              when={rows().length > 0}
              fallback={<li class="px-2.5 py-3 text-xs text-base-subtle">No matches for “{query()}”</li>}
            >
              <For each={rows()}>
                {(o, i) => {
                  const selected = () => o.value === props.value;
                  return (
                    <li role="option" id={optId(i())} aria-selected={selected()}>
                      <button
                        type="button"
                        tabindex="-1"
                        class={`w-full flex items-center gap-2 px-2.5 py-2 text-xs text-left rounded-selector transition-colors ${
                          selected()
                            ? 'bg-(--ui-state-selected) text-primary font-bold'
                            : `text-base-muted hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down) ${i() === active() ? 'bg-base-200' : ''}`
                        }`}
                        onClick={() => pick(o)}
                      >
                        <Show when={selected()} fallback={<span class="w-3.5 shrink-0" />}>
                          <Check size={14} class="shrink-0" />
                        </Show>
                        <span class="truncate">{o.label}</span>
                      </button>
                    </li>
                  );
                }}
              </For>
              {/* Honest about the cap: the server returns a slice, so a reader
                  who cannot see their option knows to keep typing rather than
                  concluding it does not exist. */}
              <Show when={!query() && results().length > 0}>
                <li class="px-2.5 pt-2 text-xs text-base-subtle">Type to search the full registry</li>
              </Show>
            </Show>
          </Show>
          </ul>
        </div>
      </div>
    </div>
  );
}
